import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Play, Pause, ShoppingCart, Loader2, CheckCircle, Share2, Eye } from 'lucide-react';
import { VIDEO, PAYMENT } from '@/constants/testIds';

const VideoPlayerPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const { user, isAdmin } = useAuth();
  
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false); // Nouvelle modal inscription
  
  // Anonymous access state
  const [anonymousAccess, setAnonymousAccess] = useState(null);
  const [hasFullAccess, setHasFullAccess] = useState(false);
  
  // Payment state
  const [paymentMethod, setPaymentMethod] = useState('ORANGE_MONEY');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    fetchVideo();
    checkAnonymousAccess(); // Vérifier accès anonyme
  }, [id]);

  // Vérifier accès anonyme depuis localStorage
  const checkAnonymousAccess = async () => {
    // Récupérer le numéro stocké en localStorage
    const storedData = localStorage.getItem(`anonymous_access_${id}`);
    
    if (storedData) {
      try {
        const { phone, expiresAt } = JSON.parse(storedData);
        
        // Vérifier si expiré localement
        if (new Date(expiresAt) < new Date()) {
          localStorage.removeItem(`anonymous_access_${id}`);
          return;
        }
        
        // Vérifier avec le backend
        const res = await api.get(`/boutique/${id}/check-anonymous-access`, {
          params: { phone_number: phone }
        });
        
        if (res.data.has_access) {
          setAnonymousAccess(res.data);
          setHasFullAccess(true);
          setPhoneNumber(phone);
          toast.success(`✅ Accès valide encore ${res.data.hours_remaining}h`);
        } else {
          localStorage.removeItem(`anonymous_access_${id}`);
        }
      } catch (error) {
        console.error('Erreur vérification accès:', error);
        localStorage.removeItem(`anonymous_access_${id}`);
      }
    }
  };

  // Poll payment status
  useEffect(() => {
    if (paymentId && paymentStatus === 'processing') {
      const interval = setInterval(checkPaymentStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [paymentId, paymentStatus]);

  const fetchVideo = async () => {
    try {
      const res = await api.get(`/boutique/${id}`);
      setVideo(res.data);
    } catch (error) {
      console.error('Erreur chargement vidéo:', error);
      toast.error('Vidéo introuvable');
      navigate('/boutique');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const res = await api.get(`/payments/${paymentId}/status`);
      setPaymentStatus(res.data.status);
      
      if (res.data.status === 'success') {
        toast.success('Paiement réussi ! Vidéo disponible dans "Mes achats"');
        setTimeout(() => navigate('/purchases'), 2000);
      } else if (res.data.status === 'failed') {
        toast.error('Le paiement a échoué');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  const handleBuy = () => {
    // Admin n'a pas besoin d'acheter - déjà accès complet
    if (isAdmin) {
      toast.success('✅ Vous avez déjà accès complet en tant qu\'Admin');
      return;
    }
    setShowPaymentModal(true);
  };

  const handlePayment = async () => {
    if (!phoneNumber) {
      toast.error('Veuillez entrer votre numéro de téléphone');
      return;
    }

    setPaymentLoading(true);
    try {
      let endpoint, payload;
      
      // Si utilisateur connecté : endpoint normal
      // Si non connecté : achat anonyme (24h)
      if (user) {
        endpoint = '/payments/initiate';
        payload = {
          video_id: id,
          payment_method: paymentMethod,
          phone_number: phoneNumber
        };
      } else {
        // Achat anonyme
        endpoint = `/boutique/${id}/purchase-anonymous`;
        payload = {
          payment_method: paymentMethod,
          phone_number: phoneNumber
        };
      }
      
      const res = await api.post(endpoint, payload);
      
      setPaymentId(res.data.id);
      setPaymentStatus(res.data.status);
      
      if (user) {
        toast.success('Paiement initié ! Veuillez confirmer sur votre téléphone');
      } else {
        toast.success('Paiement en cours... Accès 24h après confirmation');
        
        // Stocker pour accès anonyme
        const expiresAt = new Date();
        expiresAt.setHours(expiresAt.getHours() + 24);
        
        localStorage.setItem(`anonymous_access_${id}`, JSON.stringify({
          phone: phoneNumber,
          expiresAt: expiresAt.toISOString()
        }));
        
        // Polling pour vérifier paiement
        const checkInterval = setInterval(async () => {
          try {
            const checkRes = await api.get(`/boutique/${id}/check-anonymous-access`, {
              params: { phone_number: phoneNumber }
            });
            
            if (checkRes.data.has_access) {
              clearInterval(checkInterval);
              setHasFullAccess(true);
              setShowPaymentModal(false);
              toast.success('✅ Paiement confirmé ! Profitez pendant 24h');
              window.location.reload();
            }
          } catch (err) {
            console.error(err);
          }
        }, 3000);
        
        setTimeout(() => clearInterval(checkInterval), 120000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Disable right-click on video
  const disableRightClick = (e) => {
    e.preventDefault();
    return false;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#E50914]" size={48} />
      </div>
    );
  }

  if (!video) return null;

  // Admin ou utilisateur avec accès anonyme = vidéo complète
  // Sinon = preview seulement
  const videoUrl = (isAdmin || hasFullAccess)
    ? `${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${video.video_file_path}`
    : `${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${video.preview_video_path}`;

  return (
    <div className="min-h-screen pb-12">
      <Header />
      
      <main className="pt-24 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Video Player */}
        <div className="mb-8">
          <div className="relative bg-black rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full h-full"
              onContextMenu={disableRightClick}
              controlsList="nodownload"
              disablePictureInPicture
              onEnded={() => {
                // Proposer inscription si accès anonyme
                if (hasFullAccess && !user) {
                  setShowSignupModal(true);
                }
              }}
              data-testid={VIDEO.player}
            />
            
            {/* Custom controls */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/40 transition-colors cursor-pointer" onClick={handlePlayPause}>
              {!playing && (
                <div className="bg-[#E50914] rounded-full p-6 hover:bg-[#F8212C] transition-all transform hover:scale-110" data-testid={VIDEO.playButton}>
                  <Play size={48} fill="white" color="white" />
                </div>
              )}
            </div>

            {/* Badges d'accès */}
            {!isAdmin && !hasFullAccess && (
              <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-md text-sm">
                <span className="text-yellow-400 font-semibold">APERÇU 30s</span>
              </div>
            )}
            
            {isAdmin && (
              <div className="absolute top-4 right-4 bg-green-600/90 backdrop-blur-sm px-3 py-1 rounded-md text-sm">
                <span className="text-white font-semibold">👁️ ADMIN - Accès Complet</span>
              </div>
            )}
            
            {hasFullAccess && !user && (
              <div className="absolute top-4 right-4 bg-blue-600/90 backdrop-blur-sm px-3 py-1 rounded-md text-sm">
                <span className="text-white font-semibold">⏰ Accès 24h ({anonymousAccess?.hours_remaining || 0}h restantes)</span>
              </div>
            )}
          </div>
        </div>

        {/* Video Info */}
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
              {video.title}
            </h1>
            <p className="text-zinc-400 text-lg mb-4">{video.artist_name}</p>
            <p className="text-zinc-300 leading-relaxed">{video.description}</p>
          </div>

          <div>
            <div className="glass rounded-xl p-6 sticky top-24">
              <div className="mb-6">
                <div className="text-sm text-zinc-400 mb-1">Prix</div>
                <div className="text-3xl font-bold text-[#F97316]" data-testid={VIDEO.priceTag}>
                  {video.price_usd}$
                </div>
              </div>
              
              <div className="space-y-3">
                {!isAdmin && (
                  <Button
                    onClick={handleBuy}
                    className="w-full bg-[#E50914] hover:bg-[#F8212C] text-white font-semibold py-6"
                    data-testid={VIDEO.buyButton}
                  >
                    <ShoppingCart className="mr-2" size={20} />
                    Acheter maintenant
                  </Button>
                )}
                
                {isAdmin && (
                  <div className="bg-green-600/20 border border-green-600 rounded-lg p-4 text-center">
                    <Eye className="mx-auto mb-2 text-green-500" size={32} />
                    <p className="text-green-500 font-bold">Accès Admin Complet</p>
                    <p className="text-zinc-400 text-sm mt-1">Vous pouvez regarder gratuitement</p>
                  </div>
                )}

                {/* Bouton Partager */}
                <Button
                  onClick={() => {
                    const videoUrl = `${window.location.origin}/video/${id}`;
                    navigator.clipboard.writeText(videoUrl);
                    toast.success('🔗 Lien copié ! Partagez-le pour que vos amis achètent cette vidéo');
                  }}
                  variant="outline"
                  className="w-full border-[#E50914] text-[#E50914] hover:bg-[#E50914] hover:text-white font-semibold py-6"
                >
                  <Share2 className="mr-2" size={20} />
                  📤 Partager cette vidéo
                </Button>
              </div>

              <div className="mt-4 text-xs text-zinc-500 space-y-1">
                <p>✓ Vidéo avec votre filigrane personnel</p>
                <p>✓ Téléchargement sécurisé pendant 24h</p>
                <p>✓ Qualité HD originale</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="glass border-zinc-800" data-testid={PAYMENT.modal}>
          <DialogHeader>
            <DialogTitle>Paiement Mobile Money</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Choisissez votre méthode de paiement et entrez votre numéro
            </DialogDescription>
          </DialogHeader>

          {paymentStatus === 'success' ? (
            <div className="text-center py-8">
              <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Paiement réussi !</h3>
              <p className="text-zinc-400">Redirection vers vos achats...</p>
            </div>
          ) : paymentStatus === 'processing' ? (
            <div className="text-center py-8">
              <Loader2 size={64} className="text-[#E50914] animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Paiement en cours...</h3>
              <p className="text-zinc-400">Veuillez confirmer sur votre téléphone</p>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div>
                <Label>Méthode de paiement</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid={PAYMENT.methodSelect}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="ORANGE_MONEY">Orange Money</SelectItem>
                    <SelectItem value="AIRTEL_MONEY">Airtel Money</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Numéro de téléphone</Label>
                <Input
                  type="tel"
                  placeholder="+243..."
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-white"
                  data-testid={PAYMENT.phoneInput}
                />
              </div>

              <div className="bg-zinc-900 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Vidéo</span>
                  <span className="text-white">{video.title}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span className="text-zinc-400">Total</span>
                  <span className="text-[#F97316]">{video.price_fcfa} FCFA</span>
                </div>
              </div>

              <Button
                onClick={handlePayment}
                disabled={paymentLoading}
                className="w-full bg-[#E50914] hover:bg-[#F8212C]"
                data-testid={PAYMENT.confirmButton}
              >
                {paymentLoading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  'Confirmer le paiement'
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Inscription - Après visionnage anonyme */}
      <Dialog open={showSignupModal} onOpenChange={setShowSignupModal}>
        <DialogContent className="glass max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              🎉 Vidéo terminée !
            </DialogTitle>
            <DialogDescription className="text-center text-zinc-300 mt-4">
              Vous avez apprécié ? Créez votre compte pour :
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 my-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-500 mt-1" size={20} />
              <div>
                <p className="font-semibold text-white">Accès illimité</p>
                <p className="text-sm text-zinc-400">Regardez vos achats à vie</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-500 mt-1" size={20} />
              <div>
                <p className="font-semibold text-white">Historique sauvegardé</p>
                <p className="text-sm text-zinc-400">Retrouvez toutes vos vidéos</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-500 mt-1" size={20} />
              <div>
                <p className="font-semibold text-white">Accès sur tous appareils</p>
                <p className="text-sm text-zinc-400">Téléphone, tablette, ordinateur</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/auth?mode=register')}
              className="flex-1 bg-[#E50914] hover:bg-[#F8212C] font-bold"
            >
              Créer mon compte
            </Button>
            <Button
              onClick={() => setShowSignupModal(false)}
              variant="outline"
              className="flex-1 border-zinc-700"
            >
              Plus tard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideoPlayerPage;
